import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    useSearchSuggestions,
    useProductSearch,
    useShopperContext,
    useUsid,
    useCustomer,
    useCustomerId,
} from '@salesforce/commerce-sdk-react'
import {
    InputGroup,
    InputRightElement,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Box,
    Flex,
    HStack,
    useMultiStyleConfig,
    useOutsideClick,
} from '@chakra-ui/react'
import InputComponent from '../field/input-component'
import SearchSuggestions from './partials/search-suggestions'
import {
    getSessionJSONItem,
    isServer,
    noop,
    setSessionJSONItem,
} from '@salesforce/retail-react-app/app/utils/utils'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import debounce from 'lodash/debounce'
import {RECENT_SEARCH_KEY, RECENT_SEARCH_LIMIT} from '@salesforce/retail-react-app/app/constants'
import formatSuggestions from './partials/format-suggestions'
import {searchUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../custom-api/layers/default'
import {useIntl} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useBooleanCustomPreference} from '../../hooks/use-custom-preference'
import LoadingSpinner from '../loading-spinner'
import {SearchIcon} from '../icons'

const MANUALS_LIBRARY_ASSET_URL =
    'https://library.delonghigroup.com/assetbank-delonghi/action/directLinkImage'
const MODAL_SUGGESTIONS_LIMIT = 6

const normalizeSuggestionMatchValue = (value) =>
    `${value || ''}`
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()

const getModelSuffix = (productId) => `${productId || ''}`.split('.').pop().toUpperCase().trim()

const findMatchingProductSearchHit = (suggestion, hits = []) => {
    if (!Array.isArray(hits) || hits.length === 0) return undefined

    const suggestionProductId = `${suggestion?.productId || ''}`.toLowerCase().trim()
    const suggestionName = normalizeSuggestionMatchValue(
        suggestion?.name || suggestion?.productName,
    )
    const suggestionSuffix = getModelSuffix(suggestion?.productId)

    const exactIdMatch = hits.find((hit) => {
        const representedProduct = hit?.representedProduct || {}
        const candidateIds = [
            hit?.productId,
            representedProduct?.id,
            representedProduct?.productId,
            representedProduct?.c_subModelIdentifier,
            representedProduct?.c_variantIdentifier,
        ]

        return candidateIds.some(
            (candidateId) => `${candidateId || ''}`.toLowerCase() === suggestionProductId,
        )
    })
    if (exactIdMatch) return exactIdMatch

    const suffixGroupMatch = hits.find((hit) =>
        hit?.c_enabledVariationGroups?.some(
            (group) => getModelSuffix(group?.productId) === suggestionSuffix,
        ),
    )
    if (suffixGroupMatch) return suffixGroupMatch

    return hits.find((hit) => {
        const representedProduct = hit?.representedProduct || {}
        const candidateNames = [
            hit?.productName,
            representedProduct?.name,
            representedProduct?.productName,
            representedProduct?.c_defaultName,
        ]

        return candidateNames.some(
            (candidateName) => normalizeSuggestionMatchValue(candidateName) === suggestionName,
        )
    })
}

const withPidOnProductLink = (baseLink, pid) => {
    if (!baseLink || !pid) return baseLink
    if (/([?&])pid=/.test(baseLink)) return baseLink
    return `${baseLink}${baseLink.includes('?') ? '&' : '?'}pid=${encodeURIComponent(pid)}`
}

const resolveProductSuggestionLink = (suggestion, hits = []) => {
    const matchedHit = findMatchingProductSearchHit(suggestion, hits)
    const suggestionSuffix = getModelSuffix(suggestion?.productId)

    const variationGroupMatch = matchedHit?.c_enabledVariationGroups?.find(
        (group) => getModelSuffix(group?.productId) === suggestionSuffix,
    )

    const variantMatchedHit =
        hits.find((hit) => hit?.productId === variationGroupMatch?.productId) || matchedHit

    const representedProduct = variantMatchedHit?.representedProduct || variantMatchedHit || {}
    const canonicalLink =
        representedProduct?.c_variationGroup_slugUrl ||
        representedProduct?.c_slugUrl ||
        variationGroupMatch?.slugUrl ||
        variantMatchedHit?.c_variationGroup_slugUrl ||
        variantMatchedHit?.c_slugUrl ||
        suggestion?.link

    const resolvedPid =
        representedProduct?.id ||
        representedProduct?.productId ||
        variantMatchedHit?.c_variants?.[0]?.productId ||
        variantMatchedHit?.productId ||
        suggestion?.productId

    return withPidOnProductLink(canonicalLink, resolvedPid)
}

const getSuggestionProductDisplayName = (product, fallbackProduct) => {
    return (
        product?.c_variantIdentifier ||
        product?.c_defaultName ||
        fallbackProduct?.c_variantIdentifier ||
        fallbackProduct?.c_defaultName ||
        fallbackProduct?.productName ||
        product?.productName ||
        product?.name ||
        fallbackProduct?.name ||
        ''
    )
}

const safeParseJson = (value) => {
    if (!value) return null
    if (Array.isArray(value) || typeof value === 'object') return value
    if (typeof value !== 'string') return null

    try {
        return JSON.parse(value)
    } catch (error) {
        return null
    }
}

const normalizeManualLink = (manual) => {
    const rawLink = `${manual?.imageLink || manual?.link || manual?.url || ''}`
        .replace(/&amp;/g, '&')
        .trim()

    if (!rawLink) return ''
    if (/^https?:\/\//i.test(rawLink)) return rawLink

    const manualAssetId = `${manual?.code || rawLink}`.trim()
    if (!manualAssetId) return ''

    return `${MANUALS_LIBRARY_ASSET_URL}?assetId=${manualAssetId}&download=true`
}

const isInstructionManual = (manual) => {
    if (!manual || typeof manual !== 'object') return false

    const explicitInstructionFlag =
        manual?.isInstructionManual === 'true' ||
        manual?.isInstructionsForUse === 'true' ||
        manual?.isUserManual === 'true'

    if (explicitInstructionFlag) return true

    const nameLikeValue = `${
        manual?.name || manual?.manualName || manual?.title || ''
    }`.toLowerCase()
    if (
        nameLikeValue.includes('instruction') ||
        nameLikeValue.includes('instructions for use') ||
        nameLikeValue.includes('user manual')
    ) {
        return true
    }

    // Fallback used by SFCC payloads where "Product Manual" has no dedicated flag.
    const manualTypeFlags = [
        'isIntro',
        'isSafety',
        'isEnergyLabel',
        'isIngredient',
        'isQuickGuide',
        'isProductFiche',
        'isDeclarationOfConformity',
        'isCoffeeGuide',
        'isServiceManualR290',
        'isProductUsageData',
        'isStatementOfCompliance',
    ]

    return !manualTypeFlags.some((flag) => manual?.[flag] === 'true')
}

const selectManualsByLocale = (manualsByLocale, localeId) => {
    if (!Array.isArray(manualsByLocale) || manualsByLocale.length === 0) return []

    const languageCode = `${localeId || ''}`.split('-')[0]
    const exactLocale = manualsByLocale.find((entry) => entry?.language === localeId)
    if (exactLocale?.manuals?.length) return exactLocale.manuals

    const languageMatch = manualsByLocale.find((entry) =>
        entry?.language?.toLowerCase().startsWith(`${languageCode || ''}`.toLowerCase()),
    )
    if (languageMatch?.manuals?.length) return languageMatch.manuals

    const fallbackLocale = manualsByLocale.find((entry) =>
        ['x-default', 'en'].includes(entry?.language),
    )
    if (fallbackLocale?.manuals?.length) return fallbackLocale.manuals

    return manualsByLocale[0]?.manuals || []
}

const buildManualSuggestions = (hits, localeId, instructionsLabel) => {
    if (!Array.isArray(hits) || hits.length === 0) return []

    const suggestions = []
    const visitedLinks = new Set()

    for (const hit of hits) {
        const representedProduct = hit?.representedProduct || hit
        const productName = getSuggestionProductDisplayName(representedProduct, hit)
        const manualsAllLocales = safeParseJson(representedProduct?.c_manualDocumentAllCountry)
        const manualsForLocale = selectManualsByLocale(manualsAllLocales, localeId)
        const fallbackManuals = safeParseJson(representedProduct?.c_manualDocument)
        const candidateManuals =
            Array.isArray(manualsForLocale) && manualsForLocale.length > 0
                ? manualsForLocale
                : Array.isArray(fallbackManuals)
                ? fallbackManuals
                : []

        for (const manual of candidateManuals) {
            if (!isInstructionManual(manual)) continue

            const manualLink = normalizeManualLink(manual)
            if (!manualLink || visitedLinks.has(manualLink)) continue

            visitedLinks.add(manualLink)

            suggestions.push({
                type: 'manual',
                link: manualLink,
                name: `${productName || hit?.productId} - ${instructionsLabel}`,
                external: true,
            })

            if (suggestions.length >= MODAL_SUGGESTIONS_LIMIT) {
                return suggestions
            }
        }
    }

    return suggestions
}

const Search = ({
    searchInputPlaceholder,
    variant = 'default',
    shouldRemoveLabel = false,
    isManualsPageSearch = false,
    isSearchModal = false,
    isProductRegistration = false,
    setProductQuery = noop,
    setShowProductGrid = noop,
    setSuggestionRegistration = noop,
    setProductSelect,
    setSearchResults = noop,
    setCategorySuggestions,
    setModalNavigation,
    onCloseModal,
    onSubmitCustom = null,
    onSearchInputChangeCustom = null,
    onInputValueChange = noop,
    onSearchLoadingChange = noop,
    setIsSearchSubmitted = noop,
    customSuggestionData = null,
    showSpellingMessage = false,
    id = '',
    ariaLabel = 'Search Input',
    externalSearchInputRef = null,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [localIsSearchSubmitted, setLocalIsSearchSubmitted] = useState(false)
    const shouldUseLegacySuggestionsPopover = !isSearchModal
    const styles = useMultiStyleConfig('Search', {variant})
    const {addLayer} = useGoogleTagManager()
    const [searchQuery, setSearchQuery] = useState('')
    const navigate = useNavigation()
    const intl = useIntl()
    const searchButtonAriaLabel = intl.formatMessage({
        id: 'global.search',
        defaultMessage: 'Search',
    })
    const manualSuggestionLabel = intl.formatMessage({
        id: 'search.modal.instructionsSuggestionSuffix',
        defaultMessage: 'instructions',
    })
    const SUGGESTIONS_LENGTH = 5
    const MIN_SEARCH_CHARS = isSearchModal ? 3 : SUGGESTIONS_LENGTH

    const [searchStatusMessage, setSearchStatusMessage] = useState('')

    const {site, locale} = useMultiSite()
    const {currency} = useCurrency()
    const isNotShoppableCountry = useBooleanCustomPreference('notShoppableCountry')

    const customerId = useCustomerId()
    const {usid} = useUsid()
    const {data: shopperContext, isFetching: isFetchingShopperContext} = useShopperContext(
        {parameters: {usid, siteId: site.id}},
        {enabled: !isServer},
    )

    const {data: customer} = useCustomer(
        {parameters: {customerId}},
        {enabled: !!customerId && !isServer},
    )

    const {data: searchSuggestionData, isFetching: isFetchingSearchSuggestions} =
        useSearchSuggestions(
            {parameters: {q: searchQuery}},
            {enabled: searchQuery?.length >= MIN_SEARCH_CHARS},
        )

    const internalSearchInputRef = useRef()
    const searchInputRef = externalSearchInputRef || internalSearchInputRef
    const recentSearches = getSessionJSONItem(RECENT_SEARCH_KEY)

    let searchSuggestionSource =
        customSuggestionData !== null ? customSuggestionData : searchSuggestionData

    const searchSuggestions = useMemo(
        () => formatSuggestions(searchSuggestionSource, searchInputRef?.current?.value),
        [searchSuggestionSource],
    )

    // check if popover should open if we have suggestions
    useEffect(() => {
        shouldOpenPopover()
    }, [searchQuery, searchSuggestionSource])

    const searchSuggestionsAvailable =
        Boolean(searchSuggestions) &&
        (searchSuggestions?.categorySuggestions?.length ||
            searchSuggestions?.phraseSuggestions?.length ||
            searchSuggestions?.productSuggestions?.length ||
            searchSuggestions?.articleSuggestions?.length)

    const saveRecentSearch = (searchText) => {
        // Get recent searches or an empty array if undefined.
        let searches = getSessionJSONItem(RECENT_SEARCH_KEY) || []
        // Check if term is already in the saved searches
        searches = searches.filter((savedSearchTerm) => {
            return searchText.toLowerCase() !== savedSearchTerm.toLowerCase()
        })
        // Create a new array consisting of the search text and up to 4 other resent searches.
        // I'm assuming the order is newest to oldest.
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT)
        // Replace the save resent search with the updated value.
        setSessionJSONItem(RECENT_SEARCH_KEY, searches)
    }

    const debouncedSearch = useMemo(
        () =>
            debounce((input) => {
                if ((isSearchModal || isProductRegistration) && !input.endsWith('*')) {
                    input += '*'
                }
                setSearchQuery(input)
            }, 300),
        [],
    )

    const onSearchChange = async (e) => {
        const input = e.target.value
        const trimmedInput = input.trim()

        // Reset search submitted when user types (so results only show after Enter is pressed)
        if (isSearchModal && localIsSearchSubmitted) {
            setLocalIsSearchSubmitted(false)
            setIsSearchSubmitted(false)
        }

        if (trimmedInput.length >= MIN_SEARCH_CHARS) {
            if (searchQuery !== trimmedInput) {
                debouncedSearch.cancel()
                debouncedSearch(trimmedInput)
            }
        } else {
            debouncedSearch.cancel()
            if (searchQuery !== '') {
                setSearchQuery('')
            }
        }
    }

    useEffect(() => {
        return () => {
            debouncedSearch.cancel()
        }
    }, [debouncedSearch])

    const clearInput = () => {
        searchInputRef.current.blur()
        setIsOpen(false)
        setLocalIsSearchSubmitted(false)
        setIsSearchSubmitted(false)

        document.body.focus()
    }

    const refine = []
    if (isSearchModal) {
        if (!isNotShoppableCountry) refine.push('price=(0.01..9999999)')
        refine.push('c_skuEnabledForCountry=true')

        const isCorporate = shopperContext?.customQualifiers?.isCorporate === 'true'
        const isEmployee = customer?.c_isEmployee

        if (isCorporate) {
            refine.push('c_visibleByCorporate=true')
        }
        if (isEmployee) {
            refine.push('c_visibleByEmployee=true')
        }
        if (!isCorporate && !isEmployee) {
            refine.push('c_visibleByAll=true')
        }
    } else if (isProductRegistration) {
        refine.push('c_enabledForReg=true')
    }

    const {data: productSearchResult, isFetching} = useProductSearch(
        {
            parameters: {
                q: searchQuery,
                perPricebook: true,
                allVariationProperties: true,
                expand: ['represented_products', 'page_meta_tags'],
                ...{refine: refine},
                locale: locale.id,
                site: site.id,
                currency,
                limit: 8,
                c_isProductReg: isProductRegistration,
            },
        },
        {
            enabled: Boolean(
                (isSearchModal || isProductRegistration || isManualsPageSearch) &&
                    searchQuery.length >= MIN_SEARCH_CHARS,
            ),
            keepPreviousData: true,
        },
    )

    const onSubmitSearch = async (e) => {
        e.preventDefault()
        // Avoid blank spaces to be searched
        let searchText = searchInputRef.current.value.trim()
        // Avoid empty string searches
        if (searchText.length < MIN_SEARCH_CHARS) return
        if (isProductRegistration && finalSuggestions && finalSuggestions.length === 0) {
            setSearchResults(undefined)
            return
        }

        if (isProductRegistration && !searchText.endsWith('*')) {
            searchText += '*'
        }
        saveRecentSearch(searchText)

        if (isSearchModal) {
            setLocalIsSearchSubmitted(true)
            setIsSearchSubmitted(true)
            setSearchResults(productSearchResult || [])
            const searchUrl = searchUrlBuilder(searchText)
            setModalNavigation(searchUrl)
            await addLayer(
                basic_gtm_event.bind(this, {
                    event: 'internalSearch',
                    eventCategory: 'engagement',
                    eventAction: 'internalSearch-result',
                    eventLabel: searchText,
                    resultsAmount: productSearchResult?.total || 0,
                }),
            )
            return
        }

        clearInput()

        if (isProductRegistration) {
            setSearchResults(productSearchResult || [])
            setProductSelect(searchText)
        }

        if (isManualsPageSearch) {
            setProductQuery(searchText)
            setShowProductGrid(true)
        }
    }

    const closeAndNavigate = (suggestion) => {
        clearInput()
        setIsOpen(false)

        document.body.focus()

        if (isSearchModal && onCloseModal) {
            onCloseModal()
        }
        if (suggestion && isProductRegistration) {
            setSuggestionRegistration(suggestion)
            setSearchResults(suggestion.productId || [])
            setProductSelect(suggestion?.representedProduct?.id)
        }
        if (
            (suggestion?.c_variationGroup_slugUrl || suggestion?.c_slugUrl) &&
            !isManualsPageSearch &&
            !isProductRegistration
        ) {
            // TODO: replace with productUrlBuilder later
            const pid = suggestion?.c_variants?.[0]?.productId
            navigate(
                `${suggestion?.c_variationGroup_slugUrl || suggestion?.c_slugUrl}${
                    pid ? `?pid=${pid}` : ''
                }`,
            )
        }

        // FAQs redirect
        if (suggestion?.link) {
            addLayer(
                basic_gtm_event.bind(this, {
                    event: 'generic',
                    eventCategory: 'engagement',
                    eventAction: 'paperless-faq_single',
                    eventLabel: suggestion.name || '',
                }),
            )
            navigate(suggestion.link)
        }

        if (suggestion && isManualsPageSearch) navigate(`/s/${suggestion?.productId}`)
    }

    const shouldOpenPopover = () => {
        if (!shouldUseLegacySuggestionsPopover) {
            setIsOpen(false)
            return
        }

        const isInputFocused = document.activeElement === searchInputRef.current
        //const hasRecentSearches = recentSearches?.length > 0
        const hasSearchSuggestions =
            searchSuggestionsAvailable && searchInputRef.current?.value?.length > 0
        //setIsOpen(isInputFocused && (hasRecentSearches || hasSearchSuggestions))
        setIsOpen(isInputFocused && hasSearchSuggestions)
    }

    const onSearchInputChange = (e) => {
        onInputValueChange(e.target.value)
        onSearchChange(e)
        shouldOpenPopover()
    }

    useEffect(() => {
        if (!isSearchModal) return

        const trimmedQuery = searchQuery.replace(/\*+$/, '').trim()
        if (trimmedQuery.length < MIN_SEARCH_CHARS) {
            setSearchResults([])
            setCategorySuggestions([])
            return
        }

        const categorySuggestionItems = searchSuggestions?.categorySuggestions || []
        const productSuggestionItems = (searchSuggestions?.productSuggestions || []).map(
            (suggestion) => {
                return {
                    ...suggestion,
                    link: resolveProductSuggestionLink(suggestion, productSearchResult?.hits || []),
                }
            },
        )

        const hasProductResults = productSearchResult?.hits && productSearchResult.hits.length > 0

        // In search modal: only show results if user pressed Enter (localIsSearchSubmitted = true)
        if (localIsSearchSubmitted && hasProductResults) {
            // After submitting, include manuals with results
            const manualSuggestionItems = buildManualSuggestions(
                productSearchResult.hits,
                locale.id,
                manualSuggestionLabel,
            )
            setSearchResults(productSearchResult)
            setCategorySuggestions([
                ...categorySuggestionItems,
                ...manualSuggestionItems,
                ...productSuggestionItems,
            ])
        } else if (localIsSearchSubmitted && !hasProductResults) {
            // Submitted but no results
            setSearchResults(productSearchResult || [])
            setCategorySuggestions([...productSuggestionItems, ...categorySuggestionItems])
        } else {
            // Not submitted — stream product results and manual suggestions live while typing
            const manualSuggestionItems = hasProductResults
                ? buildManualSuggestions(productSearchResult.hits, locale.id, manualSuggestionLabel)
                : []
            setSearchResults(productSearchResult || [])
            setCategorySuggestions([...categorySuggestionItems, ...manualSuggestionItems])
        }

        setModalNavigation(searchUrlBuilder(trimmedQuery))
    }, [
        isSearchModal,
        searchQuery,
        localIsSearchSubmitted,
        productSearchResult,
        productSearchResult?.hits,
        searchSuggestions?.categorySuggestions,
        searchSuggestions?.productSuggestions,
        locale.id,
        setSearchResults,
        setCategorySuggestions,
        setModalNavigation,
        MIN_SEARCH_CHARS,
    ])

    useEffect(() => {
        if (!isSearchModal) return

        const trimmedQuery = searchQuery.replace(/\*+$/, '').trim()
        const hasActiveQuery = trimmedQuery.length >= MIN_SEARCH_CHARS

        onSearchLoadingChange(hasActiveQuery && (isFetching || isFetchingSearchSuggestions))
    }, [
        isSearchModal,
        searchQuery,
        isFetching,
        isFetchingSearchSuggestions,
        onSearchLoadingChange,
        MIN_SEARCH_CHARS,
    ])

    const popoverRef = useRef()
    const searchFormRef = useRef()
    const popoverContentRef = useRef(null)

    useOutsideClick({
        ref: popoverRef,
        handler: (e) => {
            if (!shouldUseLegacySuggestionsPopover) return
            if (
                !popoverRef.current?.contains(e.target) &&
                !searchFormRef.current?.contains(e.target)
            ) {
                setIsOpen(false)
            }
        },
    })

    // Override PopoverContent role from dialog to listbox (once)
    useEffect(() => {
        if (!shouldUseLegacySuggestionsPopover) return
        if (isOpen && !popoverContentRef.current) {
            const popoverContent = popoverRef.current?.querySelector('[role="dialog"]')
            if (popoverContent) {
                popoverContent.setAttribute('role', 'listbox')
                popoverContentRef.current = popoverContent
            }
        }
    }, [isOpen, shouldUseLegacySuggestionsPopover])

    const handleOnSubmitSearch = onSubmitCustom !== null ? onSubmitCustom : onSubmitSearch

    const handleOnInputChange =
        onSearchInputChangeCustom !== null ? onSearchInputChangeCustom : onSearchInputChange

    let finalSuggestions = []

    if (isSearchModal || isManualsPageSearch || isProductRegistration) {
        const filteredHits = productSearchResult?.hits?.filter(
            (hit) =>
                hit?.c_skipHit !== true &&
                !(hit?.productType?.set === true && hit?.c_bundleType !== 'Flexible') &&
                !hit.c_isHiddenCategory,
        )

        finalSuggestions = [].concat(searchSuggestions?.categorySuggestions, filteredHits)
    } else if (searchSuggestions.articleSuggestions) {
        finalSuggestions = searchSuggestions?.articleSuggestions
    }

    finalSuggestions = finalSuggestions?.filter(Boolean)

    // Update status message for screen readers when search results change
    useEffect(() => {
        if (isProductRegistration && finalSuggestions && searchQuery.length >= MIN_SEARCH_CHARS) {
            const count = finalSuggestions.length
            const message = intl.formatMessage(
                {
                    id: 'search.results_status',
                    defaultMessage:
                        '{count, plural, =0 {No results found} one {One result found} other {{count} results found}}',
                },
                {count},
            )
            setSearchStatusMessage(message)
        } else {
            setSearchStatusMessage('')
        }
    }, [finalSuggestions, searchQuery, isProductRegistration, intl, MIN_SEARCH_CHARS])

    const handleInputKeyDown = (e) => {
        if (!isOpen || !isProductRegistration) return

        if (e.key === 'Escape') {
            e.preventDefault()
            setIsOpen(false)
            searchInputRef.current?.focus()
        }
    }

    const handleFocusLastFromSuggestion = () => {
        searchInputRef.current?.focus()
    }

    const isStandaloneModalSearch = variant === 'modalSearch' && !isSearchModal
    const isManualsSearch = variant === 'manualsSearch'

    return (
        <Box {...styles.container} ref={popoverRef} id={id}>
            {/* Screen reader announcement for search results */}
            <Box
                role="status"
                aria-live="polite"
                aria-atomic="true"
                position="absolute"
                left="-10000px"
                width="1px"
                height="1px"
                overflow="hidden"
            >
                {searchStatusMessage}
            </Box>
            <Popover
                isOpen={shouldUseLegacySuggestionsPopover && isOpen}
                isLazy
                initialFocusRef={searchInputRef}
                placement="bottom-start"
                matchWidth
                closeOnBlur={true}
                onClose={() => setIsOpen(false)}
            >
                {isFetchingShopperContext && <LoadingSpinner />}
                <PopoverTrigger>
                    <form
                        ref={searchFormRef}
                        onSubmit={(e) => handleOnSubmitSearch(e, searchInputRef, finalSuggestions)}
                        style={isManualsPageSearch ? {...styles.helpTextGap} : {}}
                    >
                        <HStack
                            {...styles.searchHStack}
                            {...(isStandaloneModalSearch ? styles.searchInputRow : {})}
                        >
                            <InputGroup {...styles.searchInputGroup}>
                                <InputComponent
                                    id="search-input"
                                    name="search-input"
                                    onChange={(e) => handleOnInputChange(e)}
                                    label={searchInputPlaceholder}
                                    shouldRemoveLabel={shouldRemoveLabel}
                                    type="text"
                                    ref={searchInputRef}
                                    data-testid="search-input"
                                    role="combobox"
                                    aria-label={ariaLabel}
                                    aria-expanded={shouldUseLegacySuggestionsPopover && isOpen}
                                    aria-controls={
                                        shouldUseLegacySuggestionsPopover
                                            ? 'sf-suggestion-popover'
                                            : undefined
                                    }
                                    aria-haspopup={
                                        shouldUseLegacySuggestionsPopover ? 'listbox' : undefined
                                    }
                                    aria-autocomplete={
                                        shouldUseLegacySuggestionsPopover ? 'list' : undefined
                                    }
                                    aria-invalid={showSpellingMessage ? 'true' : 'false'}
                                    aria-describedby={
                                        showSpellingMessage ? 'search-error-message' : undefined
                                    }
                                    // iOS Safari auto-zooms inputs with font-size < 16px.
                                    // @supports (-webkit-touch-callout: none) matches only
                                    // iOS Safari, so the 16px override is invisible to every
                                    // other platform (Android, desktop stay at 12px).
                                    // We do it here in the search bar because the auto zoom blocks the user from
                                    // exiting the search bar after focusing it.
                                    sx={{
                                        '@supports (-webkit-touch-callout: none)': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                    onBlur={() => {
                                        setTimeout(() => {
                                            if (
                                                !popoverRef.current?.contains(
                                                    document.activeElement,
                                                )
                                            ) {
                                                setIsOpen(false)
                                            }
                                        }, 200)
                                    }}
                                    onKeyDown={handleInputKeyDown}
                                />
                                {isManualsSearch && (
                                    <InputRightElement
                                        data-testid="search-right-element"
                                        {...styles.rightElement}
                                    >
                                        <Box
                                            as="button"
                                            type="submit"
                                            aria-label={searchButtonAriaLabel}
                                            data-testid="search-submit-button"
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            cursor="pointer"
                                            pointerEvents="auto"
                                        >
                                            <SearchIcon />
                                        </Box>
                                    </InputRightElement>
                                )}
                            </InputGroup>
                        </HStack>
                    </form>
                </PopoverTrigger>
                {shouldUseLegacySuggestionsPopover && (
                    <PopoverContent {...styles.popoverContent} data-testid="sf-suggestion-popover">
                        <Flex
                            data-testid="sf-suggestion-popover"
                            display={isOpen || searchInputRef?.value?.length > 0 ? 'block' : 'none'}
                            {...styles.modalSuggestions}
                        >
                            {searchSuggestionSource === null || isFetching ? (
                                <LoadingSpinner />
                            ) : (
                                <SearchSuggestions
                                    closeAndNavigate={closeAndNavigate}
                                    recentSearches={recentSearches}
                                    searchSuggestions={finalSuggestions}
                                    onFocusLast={handleFocusLastFromSuggestion}
                                />
                            )}
                        </Flex>
                    </PopoverContent>
                )}
            </Popover>
        </Box>
    )
}

Search.propTypes = {
    searchInputPlaceholder: PropTypes.any,
    variant: PropTypes.string,
    shouldRemoveLabel: PropTypes.bool,
    isManualsPageSearch: PropTypes.bool,
    isSearchModal: PropTypes.bool,
    isProductRegistration: PropTypes.bool,
    setProductSelect: PropTypes.func,
    setProductQuery: PropTypes.func,
    setShowProductGrid: PropTypes.func,
    setSearchResults: PropTypes.func,
    setCategorySuggestions: PropTypes.func,
    setModalNavigation: PropTypes.func,
    setShowCategories: PropTypes.func,
    setSuggestionRegistration: PropTypes.func,
    onCloseModal: PropTypes.func,
    onSubmitCustom: PropTypes.func,
    onSearchInputChangeCustom: PropTypes.func,
    onInputValueChange: PropTypes.func,
    onSearchLoadingChange: PropTypes.func,
    setIsSearchSubmitted: PropTypes.func,
    customSuggestionData: PropTypes.object,
    showSpellingMessage: PropTypes.bool,
    id: PropTypes.string,
    ariaLabel: PropTypes.string,
    externalSearchInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({current: PropTypes.any}),
    ]),
}

export default Search
